import {
  Alert,
  Button,
  DatePicker,
  Input,
  message,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { type Dayjs } from 'dayjs';
import { RefreshCcw, Search } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { adminCommerceApi } from '@/services/adminCommerceApi';
import { authStore, brandStore } from '@/stores';
import type {
  AdminCommerceOrderDetail,
  AdminCommerceOrderListRequest,
  AdminCommerceOrderSummary,
} from '@/types';
import { OrderDetailDrawer } from './OrderDetailDrawer';

const { RangePicker } = DatePicker;

function displayTime(value: string) {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
}

function money(value: number, currency: string) {
  const normalized = currency.toUpperCase();
  const exponent = ['JPY', 'KRW'].includes(normalized)
    ? 0
    : ['BHD', 'JOD', 'KWD'].includes(normalized)
      ? 3
      : 2;
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(value / 10 ** exponent);
}

export const AdminCommerceOrdersPage = observer(() => {
  const activeBrand = brandStore.activeBrand;
  const brandId = activeBrand?.id ?? '';
  const canRead = authStore.can(ADMIN_PERMISSIONS.commerceOrdersRead);
  const [items, setItems] = useState<AdminCommerceOrderSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [orderType, setOrderType] = useState<string>();
  const [productType, setProductType] = useState<string>();
  const [orderStatus, setOrderStatus] = useState<string>();
  const [paymentStatus, setPaymentStatus] = useState<string>();
  const [stripeEnvironment, setStripeEnvironment] = useState<string>();
  const [timeRange, setTimeRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const [sortBy, setSortBy] =
    useState<AdminCommerceOrderListRequest['sortBy']>('paid_at');
  const [sortOrder, setSortOrder] =
    useState<AdminCommerceOrderListRequest['sortOrder']>('desc');
  const [cursor, setCursor] = useState('');
  const [nextCursor, setNextCursor] = useState('');
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [selectedOrderNo, setSelectedOrderNo] = useState('');
  const [detail, setDetail] = useState<AdminCommerceOrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadOrders = useCallback(async () => {
    if (!brandId || !canRead) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const result = await adminCommerceApi.listOrders({
        brandId,
        search: search.trim() || undefined,
        orderType,
        productType,
        orderStatus,
        paymentStatus,
        stripeEnvironment,
        startTime: timeRange?.[0]?.startOf('day').toISOString(),
        endTime: timeRange?.[1]?.endOf('day').toISOString(),
        sortBy,
        sortOrder,
        cursor: cursor || undefined,
        pageSize: 30,
      });
      setItems(result.items ?? []);
      setNextCursor(result.nextCursor ?? '');
    } catch {
      message.error('订单列表加载失败');
    } finally {
      setLoading(false);
    }
  }, [
    brandId,
    canRead,
    cursor,
    orderStatus,
    orderType,
    paymentStatus,
    productType,
    search,
    sortBy,
    sortOrder,
    stripeEnvironment,
    timeRange,
  ]);

  const loadDetail = useCallback(
    async (orderNo: string) => {
      if (!brandId || !orderNo) {
        return;
      }
      setDetailLoading(true);
      try {
        setDetail(await adminCommerceApi.getOrder(brandId, orderNo));
      } catch {
        message.error('订单详情加载失败');
      } finally {
        setDetailLoading(false);
      }
    },
    [brandId],
  );

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!brandId) {
      setItems([]);
    }
    setCursor('');
    setNextCursor('');
    setCursorHistory([]);
    setSelectedOrderNo('');
    setDetail(null);
  }, [brandId]);

  const columns = useMemo(
    () => [
      {
        title: '订单号',
        dataIndex: 'orderNo',
        width: 230,
        render: (value: string, row: AdminCommerceOrderSummary) => (
          <Button
            type="link"
            className="px-0"
            onClick={() => {
              setSelectedOrderNo(row.orderNo);
              void loadDetail(row.orderNo);
            }}
          >
            {value}
          </Button>
        ),
      },
      { title: '用户 ID', dataIndex: 'userId', width: 130 },
      {
        title: '类型',
        key: 'type',
        width: 190,
        render: (_: unknown, row: AdminCommerceOrderSummary) => (
          <Space direction="vertical" size={0}>
            <span>{row.orderType}</span>
            <span className="text-xs text-muted-foreground">
              {row.productType}
            </span>
          </Space>
        ),
      },
      {
        title: '状态',
        key: 'status',
        width: 150,
        render: (_: unknown, row: AdminCommerceOrderSummary) => (
          <Space direction="vertical" size={2}>
            <Tag>{row.status}</Tag>
            <span className="text-xs">{row.paymentStatus}</span>
          </Space>
        ),
      },
      {
        title: '实付 / 已退',
        key: 'amount',
        width: 150,
        render: (_: unknown, row: AdminCommerceOrderSummary) => (
          <Space direction="vertical" size={0}>
            <span>{money(row.amountTotal, row.currency)}</span>
            <span className="text-xs text-muted-foreground">
              已退 {money(row.amountRefunded, row.currency)}
            </span>
          </Space>
        ),
      },
      {
        title: '发放积分',
        dataIndex: 'creditsAmount',
        width: 120,
        render: (value: number) => value.toLocaleString(),
      },
      {
        title: '支付时间',
        dataIndex: 'paidAt',
        width: 175,
        render: displayTime,
      },
      {
        title: '环境',
        dataIndex: 'stripeEnvironment',
        width: 100,
        render: (value: string) => <Tag>{value || '-'}</Tag>,
      },
    ],
    [loadDetail],
  );

  const resetPage = () => {
    setCursor('');
    setCursorHistory([]);
  };

  const applySearch = () => {
    setSearch(searchInput.trim());
    resetPage();
  };

  const onRangeChange: RangePickerProps['onChange'] = (value) => {
    setTimeRange(value ? [value[0], value[1]] : null);
    resetPage();
  };

  if (!canRead) {
    return (
      <Alert
        type="warning"
        showIcon
        message="当前账号没有订单读取权限"
        description="需要 commerce.orders.read 或 commerce.orders.refund 权限。"
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">订单管理</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {activeBrand?.name || '当前 Brand'} · 查看历史订单、权益及退款状态
          </p>
        </div>
        <Button
          icon={<RefreshCcw size={16} />}
          onClick={() => void loadOrders()}
        >
          刷新
        </Button>
      </div>

      <div className="rounded-lg border bg-card p-4">
        <Space wrap>
          <Input
            allowClear
            prefix={<Search size={15} />}
            placeholder="订单号 / 用户 ID / Stripe ID"
            value={searchInput}
            className="w-64"
            onChange={(event) => setSearchInput(event.target.value)}
            onPressEnter={applySearch}
          />
          <Select
            allowClear
            placeholder="商品类型"
            className="w-44"
            value={productType}
            options={[
              { value: 'membership_subscription', label: '会员订阅' },
              { value: 'credits_pack', label: '积分包' },
            ]}
            onChange={(value) => {
              setProductType(value);
              resetPage();
            }}
          />
          <Select
            allowClear
            placeholder="订单类型"
            className="w-48"
            value={orderType}
            options={[
              { value: 'subscription_initial', label: '订阅首购' },
              { value: 'subscription_renewal', label: '订阅续费' },
              { value: 'credits_pack', label: '积分包' },
            ]}
            onChange={(value) => {
              setOrderType(value);
              resetPage();
            }}
          />
          <Select
            allowClear
            placeholder="订单状态"
            className="w-36"
            value={orderStatus}
            options={[
              { value: 'paid', label: '已支付' },
              { value: 'refunded', label: '已退款' },
            ]}
            onChange={(value) => {
              setOrderStatus(value);
              resetPage();
            }}
          />
          <Select
            allowClear
            placeholder="支付状态"
            className="w-36"
            value={paymentStatus}
            options={[
              { value: 'paid', label: '支付成功' },
              { value: 'unpaid', label: '未支付' },
            ]}
            onChange={(value) => {
              setPaymentStatus(value);
              resetPage();
            }}
          />
          <Select
            allowClear
            placeholder="Stripe 环境"
            className="w-36"
            value={stripeEnvironment}
            options={[
              { value: 'sandbox', label: 'Sandbox' },
              { value: 'production', label: 'Production' },
            ]}
            onChange={(value) => {
              setStripeEnvironment(value);
              resetPage();
            }}
          />
          <RangePicker value={timeRange} onChange={onRangeChange} />
          <Select
            value={`${sortBy}:${sortOrder}`}
            className="w-40"
            options={[
              { value: 'paid_at:desc', label: '支付时间倒序' },
              { value: 'paid_at:asc', label: '支付时间正序' },
              { value: 'created_at:desc', label: '创建时间倒序' },
              { value: 'amount_total:desc', label: '金额从高到低' },
            ]}
            onChange={(value) => {
              const [nextSortBy, nextSortOrder] = value.split(':');
              setSortBy(nextSortBy as AdminCommerceOrderListRequest['sortBy']);
              setSortOrder(
                nextSortOrder as AdminCommerceOrderListRequest['sortOrder'],
              );
              resetPage();
            }}
          />
          <Button type="primary" onClick={applySearch}>
            查询
          </Button>
        </Space>
      </div>

      <Table
        rowKey="orderNo"
        loading={loading}
        dataSource={items}
        columns={columns}
        scroll={{ x: 1250 }}
        pagination={false}
      />
      <div className="flex justify-end gap-2">
        <Button
          disabled={cursorHistory.length === 0}
          onClick={() => {
            const history = [...cursorHistory];
            setCursor(history.pop() ?? '');
            setCursorHistory(history);
          }}
        >
          上一页
        </Button>
        <Button
          disabled={!nextCursor}
          onClick={() => {
            setCursorHistory((history) => [...history, cursor]);
            setCursor(nextCursor);
          }}
        >
          下一页
        </Button>
      </div>

      <OrderDetailDrawer
        open={Boolean(selectedOrderNo)}
        loading={detailLoading}
        brandId={brandId}
        detail={detail}
        onClose={() => {
          setSelectedOrderNo('');
          setDetail(null);
        }}
        onRefresh={() => {
          void loadOrders();
          if (selectedOrderNo) {
            void loadDetail(selectedOrderNo);
          }
        }}
      />
    </div>
  );
});
